// The first page of a food-truck restaurant with several trucks: the hero,
// "Deals for you", and "Find a truck" — every truck as a card beside a map,
// with where it is parked and a way to order from it or see its schedule.
//
// Choosing a card highlights its pin and moves the map to it; choosing a pin
// highlights its card and brings the card into view.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { Box, CircularProgress } from "@mui/material";
import { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import DeliveryDiningOutlinedIcon from "@mui/icons-material/DeliveryDiningOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import DirectionsOutlinedIcon from "@mui/icons-material/DirectionsOutlined";
import Map from "../MapWhenReady/MapWhenReady";
import DealBanners from "../RestaurantDetailComponent/Storefront/DealBanners";
import { StatusChip } from "../RestaurantDetailComponent/Storefront/RestaurantHero";
import { foodTrucks } from "../../apollo/server";
import { resizedImage, resizedSrcSet } from "../../utils/resizedImage";
import { shortAddress, titleCaseSpot } from "../../utils/truckText";
import {
  BUTTON_RESET, C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS, SHADOW, TYPE, topBarOffset,
} from "../../utils/designTokens";

const FOOD_TRUCKS = gql`${foodTrucks}`;

const SECTION_GAP = "36px";
const CARD_RADIUS = "22px";
const TRUCK_ORANGE = "#E36A1A";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const initialsOf = (name) =>
  (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

// Where a truck is (open) or usually parks next (closed), with its map point.
function spotOf(truck) {
  const stop = truck.isOpenNow ? truck.currentStop : truck.nextStop || truck.currentStop;
  if (!stop) return null;
  const lat = Number(stop.latitude);
  const lng = Number(stop.longitude);
  return {
    stop,
    name: titleCaseSpot(stop.label) || shortAddress(stop.address),
    address: stop.label ? shortAddress(stop.address) : null,
    point: Number.isFinite(lat) && Number.isFinite(lng) && (lat || lng) ? { lat, lng } : null,
  };
}

// "Open now · until 9:00 PM" / "Back Tuesday at 11:00 AM".
function hoursOf(truck) {
  if (truck.isOpenNow) {
    const end = (truck.currentStop?.windowLabel || "").split("–").pop().trim();
    return end ? `Open now · until ${end}` : "Open now";
  }
  return (truck.statusLabel || "No stops scheduled").split(" · ")[0];
}

function directionsUrl(spot) {
  if (spot?.point) return `https://www.google.com/maps/dir/?api=1&destination=${spot.point.lat},${spot.point.lng}`;
  if (spot?.stop?.address) return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(spot.stop.address)}`;
  return null;
}

const roundButton = {
  ...BUTTON_RESET,
  width: 44,
  height: 44,
  borderRadius: "50%",
  bgcolor: C.bg,
  color: C.ink,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: SHADOW.button,
  transition: "transform .15s ease",
  "&:hover": { transform: "scale(1.05)" },
  ...FOCUS_RING,
};

const buttonBase = {
  ...BUTTON_RESET,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  height: 44,
  px: 2.25,
  borderRadius: RADIUS.control,
  fontFamily: FONT_BODY,
  fontSize: 15,
  fontWeight: 700,
  whiteSpace: "nowrap",
  textDecoration: "none",
  ...FOCUS_RING,
};
const redButton = { ...buttonBase, bgcolor: C.red, color: "#FFFFFF", "&:hover": { bgcolor: C.redHover } };
const outlineButton = { ...buttonBase, border: `1.5px solid ${C.ink}`, color: C.ink, bgcolor: C.bg, "&:hover": { bgcolor: C.surface } };
const lightOutlineButton = { ...buttonBase, border: `1px solid ${C.borderStrong}`, color: C.ink, bgcolor: C.bg, fontWeight: 600, "&:hover": { bgcolor: C.surface } };

/* ------------------------------------------------------------------ hero */

function TruckHero({ restaurant, cuisineLine, trucks, openTrucks, isFavourite, onToggleFavourite, onShare, onPrimary }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const name = restaurant?.name || "";
  const firstOpen = openTrucks[0];
  const chips = [];
  chips.push(
    openTrucks.length
      ? { key: "open", tone: "open", label: `${plural(openTrucks.length, "truck", "trucks")} open now` }
      : { key: "open", tone: "closed", icon: ScheduleRoundedIcon, label: "All trucks closed" }
  );
  chips.push({ key: "count", tone: "neutral", icon: LocalShippingRoundedIcon, label: plural(trucks.length, "truck", "trucks") });
  chips.push({ key: "pickup", tone: "neutral", icon: StorefrontOutlinedIcon, label: "Pickup" });
  if (restaurant?.mobileDeliveryEnabled) chips.push({ key: "delivery", tone: "neutral", icon: DeliveryDiningOutlinedIcon, label: "Delivery" });

  return (
    <Box component="header" sx={{ position: "relative" }}>
      <Box sx={{ position: "relative", height: { xs: 240, sm: 280, md: 320 }, borderRadius: "26px", overflow: "hidden", bgcolor: C.surface2 }}>
        {restaurant?.image && (
          <Box
            component="img"
            src={resizedImage(restaurant.image, 1200)}
            srcSet={resizedSrcSet(restaurant.image, [480, 800, 1200, 1600])}
            sizes="(min-width: 1280px) 1232px, 100vw"
            alt={`${name} cover photo`}
            fetchpriority="high"
            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        {/* A light dark strip along the bottom, for contrast under the card. */}
        <Box aria-hidden="true" sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "45%", background: "linear-gradient(to top, rgba(26,25,24,0.35), rgba(26,25,24,0))" }} />
        <Box
          sx={{
            position: "absolute", top: 16, left: 16, display: "inline-flex", alignItems: "center", gap: 0.75,
            height: 34, px: 1.5, borderRadius: RADIUS.pill, bgcolor: "#FFFFFF", color: C.ink,
            fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, boxShadow: SHADOW.button,
          }}
        >
          <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: TRUCK_ORANGE }} />
          Food trucks
        </Box>
        <Box sx={{ position: "absolute", top: 16, right: 16, display: "flex", gap: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={onToggleFavourite}
            aria-pressed={isFavourite}
            aria-label={isFavourite ? `Remove ${name} from favourites` : `Add ${name} to favourites`}
            sx={roundButton}
          >
            {isFavourite ? <FavoriteRoundedIcon sx={{ fontSize: 22, color: C.red }} /> : <FavoriteBorderRoundedIcon sx={{ fontSize: 22 }} />}
          </Box>
          <Box component="button" type="button" onClick={onShare} aria-label={`Share ${name}`} sx={roundButton}>
            <IosShareRoundedIcon sx={{ fontSize: 21 }} />
          </Box>
        </Box>
      </Box>

      {/* Info card, overlapping the banner by 72px; the logo tile sits 60px
          above its top edge. */}
      <Box
        sx={{
          position: "relative",
          mt: "-72px",
          mx: { xs: 1.5, sm: 3 },
          px: { xs: 2.5, md: 3.5 },
          pt: "52px",
          pb: { xs: 2.5, md: 3 },
          borderRadius: CARD_RADIUS,
          bgcolor: C.bg,
          boxShadow: "0 10px 34px rgba(26,25,24,0.12)",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-end" },
          gap: 2.5,
        }}
      >
        <Box
          sx={{
            position: "absolute", top: "-60px", left: { xs: 20, md: 28 }, width: 96, height: 96,
            borderRadius: "24px", border: "4px solid #FFFFFF", boxSizing: "border-box", overflow: "hidden",
            bgcolor: C.ink, boxShadow: SHADOW.button, display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {restaurant?.logo && !logoFailed ? (
            <Box component="img" src={resizedImage(restaurant.logo, 240)} alt={`${name} logo`} onError={() => setLogoFailed(true)}
              sx={{ width: "100%", height: "100%", objectFit: "cover", bgcolor: "#FFFFFF" }} />
          ) : (
            <Box component="span" role="img" aria-label={`${name} logo`} sx={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 32, color: C.goldAccent }}>
              {initialsOf(name)}
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box component="h1" sx={{ m: 0, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: { xs: 28, md: 36 }, lineHeight: 1.1, letterSpacing: "-0.02em", color: C.ink, overflowWrap: "anywhere" }}>
            {name}
          </Box>
          {cuisineLine && (
            <Box component="p" sx={{ m: 0, mt: 0.5, fontFamily: FONT_BODY, fontSize: 15, color: C.muted }}>{cuisineLine}</Box>
          )}
          <Box component="ul" aria-label="Trucks" sx={{ display: "flex", flexWrap: "wrap", gap: 1, m: 0, mt: 1.5, p: 0 }}>
            {chips.map((c) => (
              <StatusChip key={c.key} tone={c.tone} icon={c.icon}>{c.label}</StatusChip>
            ))}
          </Box>
        </Box>

        <Box
          component="button"
          type="button"
          onClick={onPrimary}
          sx={{ ...redButton, height: 52, px: 3, fontSize: 16, width: { xs: "100%", md: "auto" }, flexShrink: 0 }}
        >
          {firstOpen ? `Order from ${firstOpen.name} →` : "See truck schedules"}
        </Box>
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------- truck card */

function TruckCard({ truck, fallbackImage, selected, onSelect, onSchedule, cardRef }) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const spot = spotOf(truck);
  const open = !!truck.isOpenNow;
  const photo = !photoFailed ? truck.imageUrl || fallbackImage : fallbackImage;
  const directions = directionsUrl(spot);
  const stop = (e) => e.stopPropagation();

  return (
    <Box
      component="article"
      ref={cardRef}
      onClick={() => onSelect(truck._id)}
      aria-label={`${truck.name}, ${open ? "open now" : "closed now"}`}
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "minmax(200px, 38%) minmax(0, 1fr)" },
        borderRadius: CARD_RADIUS,
        border: `1px solid ${selected ? C.red : C.border}`,
        boxShadow: selected ? `0 0 0 1px ${C.red}, 0 10px 28px rgba(196,43,31,0.16)` : "none",
        bgcolor: C.bg,
        overflow: "hidden",
        cursor: "pointer",
        scrollMarginTop: topBarOffset(16),
        transition: "box-shadow .2s ease, border-color .2s ease",
      }}
    >
      <Box sx={{ position: "relative", minHeight: 200, bgcolor: C.surface2 }}>
        {photo ? (
          <Box
            component="img"
            src={resizedImage(photo, 800)}
            alt={spot ? `${truck.name} at ${spot.name}` : truck.name}
            loading="lazy"
            onError={() => setPhotoFailed(true)}
            sx={{
              position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block",
              filter: open ? "none" : "grayscale(40%) brightness(0.9)",
            }}
          />
        ) : (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 56, color: C.borderStrong }} />
          </Box>
        )}
        <Box
          sx={{
            position: "absolute", top: 12, left: 12, display: "inline-flex", alignItems: "center", gap: 0.75,
            height: 28, px: 1.25, borderRadius: RADIUS.pill, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700,
            bgcolor: open ? C.greenSoft : "#FFFFFF", color: open ? C.green : C.muted,
          }}
        >
          {open && <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: C.green }} />}
          {open ? "Open now" : "Closed now"}
        </Box>
        <Box
          sx={{
            position: "absolute", left: 12, bottom: 12, display: "inline-flex", alignItems: "center", gap: 0.75,
            maxWidth: "calc(100% - 24px)", height: 32, px: 1.25, borderRadius: "10px", bgcolor: C.ink, color: "#FFFFFF",
            fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700,
          }}
        >
          <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 18, color: C.goldAccent }} />
          <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{truck.name}</Box>
        </Box>
      </Box>

      <Box sx={{ p: { xs: 2.25, md: 2.75 }, display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0 }}>
        <Box sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted }}>
          {open ? "Parked today at" : "Usually parked at"}
        </Box>
        <Box component="h3" sx={{ m: 0, mt: -0.75 }}>
          {/* The keyboard way to choose this truck (the whole card takes clicks). */}
          <Box
            component="button"
            type="button"
            onClick={(e) => { stop(e); onSelect(truck._id); }}
            aria-pressed={selected}
            sx={{
              ...BUTTON_RESET, ...FOCUS_RING, borderRadius: "6px",
              fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.015em",
              color: C.ink, overflowWrap: "anywhere",
            }}
          >
            {spot ? spot.name : "No stops scheduled"}
          </Box>
        </Box>
        {spot?.address && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, ...TYPE.body, color: C.muted }}>
            <PlaceOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, mt: "1px", color: C.muted }} />
            <Box component="span" sx={{ minWidth: 0, overflowWrap: "anywhere" }}>{spot.address}</Box>
          </Box>
        )}
        <Box
          sx={{
            display: "inline-flex", alignSelf: "flex-start", alignItems: "center", gap: 0.75, px: 1.5, py: 1,
            borderRadius: "14px", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700,
            bgcolor: open ? C.greenSoft : C.goldSoft, color: open ? C.green : C.gold,
          }}
        >
          <AccessTimeRoundedIcon aria-hidden="true" sx={{ fontSize: 18 }} />
          {hoursOf(truck)}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: "auto", pt: 0.5 }}>
          {open && truck.currentStop ? (
            <Box component="button" type="button" onClick={(e) => { stop(e); onSchedule(truck); }} sx={redButton}>
              Order now →
            </Box>
          ) : (
            <Box component="button" type="button" onClick={(e) => { stop(e); onSchedule(truck); }} sx={outlineButton}>
              Schedule &amp; menu →
            </Box>
          )}
          {directions && (
            <Box
              component="a"
              href={directions}
              target="_blank"
              rel="noopener noreferrer"
              onClick={stop}
              aria-label={`Directions to ${truck.name}${spot ? ` at ${spot.name}` : ""} (opens maps)`}
              sx={lightOutlineButton}
            >
              <DirectionsOutlinedIcon aria-hidden="true" sx={{ fontSize: 19 }} />
              Directions
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/* -------------------------------------------------------------------- map */

function TruckPin({ truck, selected, onSelect }) {
  const open = !!truck.isOpenNow;
  const bg = selected ? C.red : open ? C.green : "#6E6A63";
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box
        component="button"
        type="button"
        onClick={() => onSelect(truck._id)}
        aria-pressed={selected}
        aria-label={`Show ${truck.name}`}
        sx={{
          ...BUTTON_RESET,
          display: "inline-flex", alignItems: "center", gap: 0.5, maxWidth: 200, px: 1.25, py: 0.5,
          borderRadius: RADIUS.pill, bgcolor: bg, color: "#FFFFFF", border: "2px solid #FFFFFF",
          fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
          boxShadow: "0 2px 8px rgba(26,25,24,0.3)",
          "&:focus-visible": { outline: `3px solid ${C.ink}`, outlineOffset: "2px" },
        }}
      >
        <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{`${truck.name} · ${open ? "Open" : "Closed"}`}</Box>
      </Box>
      <Box aria-hidden="true" sx={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderTop: `7px solid ${bg}` }} />
    </Box>
  );
}

function TruckMap({ trucks, selectedId, onSelect, mapRef }) {
  const placed = trucks.map((t) => ({ t, at: spotOf(t)?.point })).filter((p) => p.at);
  const initialView = useMemo(() => {
    if (placed.length > 1) {
      const lngs = placed.map((p) => p.at.lng);
      const lats = placed.map((p) => p.at.lat);
      return {
        bounds: [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
        fitBoundsOptions: { padding: { top: 64, bottom: 40, left: 90, right: 90 }, maxZoom: 14 },
      };
    }
    return { longitude: placed[0]?.at.lng ?? -71.06, latitude: placed[0]?.at.lat ?? 42.36, zoom: placed.length ? 13 : 9 };
    // The map only reads this on mount; later moves are fly-tos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed.length]);

  return (
    <Box
      sx={{
        position: { md: "sticky" },
        top: { md: topBarOffset(16) },
        height: { xs: 420, md: `calc(100vh - var(--rmf-topbar-h, 64px) - 32px)` },
        minHeight: 420,
        maxHeight: { md: 720 },
        borderRadius: CARD_RADIUS,
        overflow: "hidden",
        border: `1px solid ${C.border}`,
        bgcolor: C.surface2,
      }}
    >
      <Map ref={mapRef} initialViewState={initialView} style={{ height: "100%", width: "100%" }} mapStyle="mapbox://styles/mapbox/streets-v12">
        <NavigationControl position="bottom-right" showCompass={false} />
        {placed.map(({ t, at }) => {
          const selected = String(t._id) === String(selectedId);
          return (
            <Marker key={t._id} longitude={at.lng} latitude={at.lat} anchor="bottom" style={{ zIndex: selected ? 3 : t.isOpenNow ? 2 : 1 }}>
              <TruckPin truck={t} selected={selected} onSelect={onSelect} />
            </Marker>
          );
        })}
      </Map>
    </Box>
  );
}

/* ------------------------------------------------------------------- page */

function FilterChip({ selected, onClick, children }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        ...BUTTON_RESET, ...FOCUS_RING,
        height: 40, px: 2, borderRadius: RADIUS.pill, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 600,
        border: `1px solid ${selected ? C.ink : C.borderStrong}`,
        bgcolor: selected ? C.ink : C.bg, color: selected ? "#FFFFFF" : C.ink,
        "&:hover": { bgcolor: selected ? C.ink : C.surface },
      }}
    >
      {children}
    </Box>
  );
}

// The truck that opens soonest, for the empty "Open now" list.
function nextOpening(trucks) {
  const upcoming = trucks
    .filter((t) => t.nextStop && t.nextStopDate)
    .sort((a, b) => a.nextStopDate.localeCompare(b.nextStopDate) || (a.nextStop.startMinute || 0) - (b.nextStop.startMinute || 0));
  const t = upcoming[0];
  return t ? `${t.name} · ${hoursOf(t)}` : null;
}

export default function TruckLanding({
  restaurant, cuisineLine, promos = [], rewardsPoints, showSeeAllDeals = true,
  isFavourite, onToggleFavourite, onShare, onOpenTruck,
}) {
  const { data, loading, error } = useQuery(FOOD_TRUCKS, {
    variables: { slug: restaurant?.slug },
    skip: !restaurant?.slug,
    fetchPolicy: "cache-and-network",
  });
  const trucks = useMemo(() => data?.foodTrucks || [], [data]);
  const openTrucks = useMemo(() => trucks.filter((t) => t.isOpenNow), [trucks]);
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const shown = filter === "open" ? openTrucks : trucks;

  const sectionRef = useRef(null);
  const mapRef = useRef(null);
  const cardRefs = useRef({});

  // The first open truck starts selected, else the first truck.
  useEffect(() => {
    if (selectedId == null && trucks.length) setSelectedId((openTrucks[0] || trucks[0])._id);
  }, [trucks, openTrucks, selectedId]);

  const flyTo = (id) => {
    const t = trucks.find((x) => String(x._id) === String(id));
    const at = t && spotOf(t)?.point;
    const map = mapRef.current;
    if (at && map) map.flyTo({ center: [at.lng, at.lat], zoom: Math.max(map.getZoom ? map.getZoom() : 13, 13), duration: 700 });
  };
  const selectFromCard = (id) => { setSelectedId(id); flyTo(id); };
  const selectFromPin = (id) => {
    setSelectedId(id);
    const card = cardRefs.current[id];
    if (filter === "open" && !openTrucks.some((t) => String(t._id) === String(id))) setFilter("all");
    // After a filter change the card mounts on the next frame.
    requestAnimationFrame(() => (cardRefs.current[id] || card)?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
  };

  const onPrimary = () => {
    const first = openTrucks[0];
    if (first) {
      setFilter("all");
      selectFromCard(first._id);
      requestAnimationFrame(() => cardRefs.current[first._id]?.scrollIntoView({ behavior: "smooth", block: "center" }));
    } else {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Our deals after the truck's own; the truck's are orange.
  const orderedPromos = useMemo(
    () => [...promos.filter((p) => p.restaurantId), ...promos.filter((p) => !p.restaurantId)],
    [promos]
  );

  return (
    <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 3 }, pt: topBarOffset(16), pb: 8 }}>
      <TruckHero
        restaurant={restaurant}
        cuisineLine={cuisineLine}
        trucks={trucks}
        openTrucks={openTrucks}
        isFavourite={isFavourite}
        onToggleFavourite={onToggleFavourite}
        onShare={onShare}
        onPrimary={onPrimary}
      />

      <DealBanners
        promos={orderedPromos}
        rewardsPoints={rewardsPoints}
        seeAllTo={showSeeAllDeals ? "/deals" : undefined}
        sectionSx={{ mt: SECTION_GAP }}
      />

      <Box component="section" ref={sectionRef} aria-labelledby="find-a-truck" sx={{ mt: SECTION_GAP, scrollMarginTop: topBarOffset(16) }}>
        <Box component="h2" id="find-a-truck" sx={{ ...TYPE.sectionTitle, fontSize: { xs: 24, md: 28 }, fontWeight: 800, m: 0, color: C.ink }}>
          Find a truck
        </Box>
        <Box component="p" sx={{ ...TYPE.body, fontSize: 15, m: 0, mt: 0.5, color: C.muted }}>
          Pick a truck to see where it's parked and what's on its menu
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          <FilterChip selected={filter === "all"} onClick={() => setFilter("all")}>{`All trucks · ${trucks.length}`}</FilterChip>
          <FilterChip selected={filter === "open"} onClick={() => setFilter("open")}>{`Open now · ${openTrucks.length}`}</FilterChip>
        </Box>

        {loading && !data ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: C.red }} /></Box>
        ) : error ? (
          <Box sx={{ mt: 2.5, p: 4, borderRadius: CARD_RADIUS, border: `1px dashed ${C.borderStrong}`, textAlign: "center", ...TYPE.body, color: C.muted }}>
            We couldn't load the trucks. Please refresh the page.
          </Box>
        ) : (
          <Box
            sx={{
              mt: 2.5,
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(340px, 40%)" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              {shown.length ? (
                shown.map((t) => (
                  <TruckCard
                    key={t._id}
                    truck={t}
                    fallbackImage={restaurant?.image}
                    selected={String(t._id) === String(selectedId)}
                    onSelect={selectFromCard}
                    onSchedule={(truck) => onOpenTruck(truck._id)}
                    cardRef={(el) => { cardRefs.current[t._id] = el; }}
                  />
                ))
              ) : (
                <Box sx={{ p: 4, borderRadius: CARD_RADIUS, border: `1px dashed ${C.borderStrong}`, textAlign: "center" }}>
                  <Box sx={{ fontFamily: FONT_DISPLAY, fontSize: 20, fontWeight: 800, color: C.ink }}>
                    {filter === "open" ? "No trucks are open right now" : "No trucks yet"}
                  </Box>
                  {filter === "open" && nextOpening(trucks) && (
                    <Box sx={{ ...TYPE.body, mt: 0.75, color: C.muted }}>{`Next opening: ${nextOpening(trucks)}`}</Box>
                  )}
                </Box>
              )}
            </Box>
            <TruckMap trucks={trucks} selectedId={selectedId} onSelect={selectFromPin} mapRef={mapRef} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
